import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BarChart3, Loader2, TrendingUp, Eye, MousePointerClick } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
const VIEW_COLORS = ['#22d3ee', '#fbbf24', '#34d399', '#f87171', '#a78bfa', '#f472b6', '#67e8f9', '#fb923c'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900/95 border border-blue-500/30 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-blue-300/70 mb-1">{new Date(label).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-white" style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const BackgroundClicksChart = ({ userId, token, backgrounds, isAdmin }) => {
  const [chartData, setChartData] = useState([]);
  const [bgIds, setBgIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalClicks, setTotalClicks] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [mode, setMode] = useState('views'); // 'views' or 'clicks'

  useEffect(() => {
    if (!userId || !token) return;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const endpoint = isAdmin && userId === 'default'
          ? `${API}/admin/default-backgrounds-stats`
          : `${API}/user/${userId}/backgrounds-stats`;
        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChartData(res.data.chart_data || []);
        setBgIds(res.data.bg_ids || []);
        setTotalClicks(res.data.chart_data?.reduce((sum, d) => sum + (d.total || 0), 0) || 0);
        setTotalViews(res.data.total_views || res.data.chart_data?.reduce((sum, d) => sum + (d.total_views || 0), 0) || 0);
      } catch (e) {
        console.error('Error fetching stats:', e);
      }
      setLoading(false);
    };
    fetchStats();
  }, [userId, token]);

  const getBgName = (bgId) => {
    const idx = backgrounds?.findIndex(bg => bg.id === bgId);
    if (idx >= 0) return `Fond #${idx + 1}`;
    return bgId;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-6 flex justify-center">
        <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (totalClicks === 0 && totalViews === 0) {
    return (
      <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-6 text-center">
        <BarChart3 className="w-8 h-8 text-blue-300/30 mx-auto mb-2" />
        <p className="text-blue-300/50 text-sm">Aucune statistique ces 30 derniers jours</p>
        <p className="text-blue-300/30 text-xs mt-1">Les affichages et clics apparaitront ici quand des visiteurs verront vos fonds.</p>
      </div>
    );
  }

  const isViewMode = mode === 'views';

  return (
    <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-5" data-testid="bg-clicks-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">Statistiques des fonds — 30 jours</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMode('views')}
            className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-all ${isViewMode ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'text-blue-300/50 hover:text-blue-300'}`}
            data-testid="chart-mode-views"
          >
            <Eye className="w-3 h-3" /> {totalViews} affichages
          </button>
          <button
            onClick={() => setMode('clicks')}
            className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-all ${!isViewMode ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-blue-300/50 hover:text-blue-300'}`}
            data-testid="chart-mode-clicks"
          >
            <MousePointerClick className="w-3 h-3" /> {totalClicks} clics
          </button>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(59,130,246,0.08)" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fill: 'rgba(147,197,253,0.4)', fontSize: 11 }}
            axisLine={{ stroke: 'rgba(59,130,246,0.15)' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: 'rgba(147,197,253,0.4)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59,130,246,0.08)' }} />
          <Legend
            formatter={(value) => <span className="text-xs text-blue-300/60">{value}</span>}
            iconSize={10}
          />
          {bgIds.map((bgId, i) => (
            <Bar
              key={isViewMode ? `${bgId}_views` : bgId}
              dataKey={isViewMode ? `${bgId}_views` : bgId}
              name={`${getBgName(bgId)} ${isViewMode ? '(vues)' : '(clics)'}`}
              fill={isViewMode ? VIEW_COLORS[i % VIEW_COLORS.length] : BG_COLORS[i % BG_COLORS.length]}
              radius={[3, 3, 0, 0]}
              stackId="stack"
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BackgroundClicksChart;
